import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import RequirePermission from "../../../auth/RequirePermission";
import { ADMIN_PERMISSIONS } from "../../../auth/adminPermissions";
import { useAuth } from "../../../auth/useAuth";
import AdminPageLayout from "../../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../../components/midpoint/admin/layout/AdminPageHeader";
import AdminStatePanel from "../../../components/midpoint/admin/layout/AdminStatePanel";
import { ConfirmDialog } from "../listings/listingAdminShared";
import { AdminAutomotiveNav } from "../automotive/AdminAutomotiveNav";
import SellerSubmissionImageReviewSection from "../../../components/admin/seller-submissions/SellerSubmissionImageReviewSection";
import SellerSubmissionCommunicationPanel from "../../../components/admin/seller-submissions/SellerSubmissionCommunicationPanel";
import SubmissionContextSection from "../../../components/admin/SubmissionContextSection";
import {
  SELLER_SUBMISSION_ADMIN_PATH,
  SELLER_WORKFLOW_ACTIONS,
  SELLER_WORKFLOW_ACTION_LABELS_ES,
  formatAdminDate,
  formatCopPrice,
  formatSellerReviewStatus,
  formatSellerSpamStatus,
  formatSellerSubmissionStatus,
  formatSellerType,
  formatSellerWorkflowStatus,
  formatVehicleSummary,
  sellerReviewStatusBadgeClass,
  sellerWorkflowStatusBadgeClass,
} from "../../../constants/sellerSubmissionAdminConstants";
import {
  fetchAdminSellerSubmission,
  updateAdminSellerSubmissionNotes,
  updateAdminSellerSubmissionWorkflow,
  convertAdminSellerSubmissionToListing,
} from "../../../services/sellerSubmissionAdminApi";
import { fetchAdminListingDraftReview } from "../../../services/automapDealerInventoryApi";
import {
  getSellerSubmissionConversionEligibility,
  sellerConversionBlockMessage,
} from "../../../utils/sellerIntake/sellerSubmissionConversionEligibility";
import {
  draftReviewStatusBadgeClass,
  formatDraftReviewStatus,
} from "../../../constants/draftListingReviewConstants";

function DetailSection({ title, children }) {
  return (
    <section className="dealer-state-box">
      <h3>{title}</h3>
      {children}
    </section>
  );
}

function DetailRow({ label, value }) {
  return (
    <p>
      <strong>{label}:</strong> {value ?? "—"}
    </p>
  );
}

const CONFIRM_ACTIONS = new Set(["reject", "archive", "mark_spam"]);
const NOTE_ACTIONS = new Set(["mark_needs_info", "mark_contacted", "reject", "archive", "mark_spam"]);

function AdminSellerSubmissionDetailPageContent() {
  const { submissionId } = useParams();
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage = isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  const [submission, setSubmission] = useState(null);
  const [notesDraft, setNotesDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingNotes, setSavingNotes] = useState(false);
  const [workflowBusy, setWorkflowBusy] = useState(false);
  const [error, setError] = useState("");
  const [notesMessage, setNotesMessage] = useState("");
  const [workflowMessage, setWorkflowMessage] = useState("");
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmReason, setConfirmReason] = useState("");
  const [conversionMessage, setConversionMessage] = useState("");
  const [converting, setConverting] = useState(false);
  const [draftReview, setDraftReview] = useState(null);

  const mergeSubmission = useCallback((next) => {
    setSubmission((current) => ({
      ...next,
      images: next?.images ?? current?.images ?? [],
      imageReview: next?.imageReview ?? current?.imageReview ?? null,
      communication: next?.communication ?? current?.communication ?? null,
    }));
  }, []);

  const loadSubmission = useCallback(() => {
    setLoading(true);
    setError("");
    fetchAdminSellerSubmission(submissionId)
      .then((data) => {
        mergeSubmission(data.submission);
        setNotesDraft(data.submission?.admin?.internalNotes || "");
      })
      .catch((err) => setError(err.message || "No pudimos cargar la solicitud."))
      .finally(() => setLoading(false));
  }, [submissionId]);

  useEffect(() => {
    const listingId = submission?.admin?.convertedListingId;
    if (!listingId) {
      setDraftReview(null);
      return;
    }
    fetchAdminListingDraftReview(listingId)
      .then((data) => setDraftReview(data.review))
      .catch(() => setDraftReview(null));
  }, [submission?.admin?.convertedListingId]);

  useEffect(() => {
    loadSubmission();
  }, [loadSubmission]);

  const runWorkflowAction = async (action, extra = {}) => {
    if (!canManage) return;
    setWorkflowBusy(true);
    setWorkflowMessage("");
    try {
      const data = await updateAdminSellerSubmissionWorkflow(submissionId, {
        action,
        ...extra,
      });
      mergeSubmission(data.submission);
      setNotesDraft(data.submission?.admin?.internalNotes || "");
      setWorkflowMessage("Estado actualizado.");
    } catch (err) {
      setWorkflowMessage(err.message || "No pudimos actualizar el estado.");
    } finally {
      setWorkflowBusy(false);
      setConfirmAction(null);
      setConfirmReason("");
    }
  };

  const openAction = (actionKey) => {
    const needsConfirm = CONFIRM_ACTIONS.has(actionKey);
    const needsReason = NOTE_ACTIONS.has(actionKey);
    if (needsConfirm) {
      setConfirmAction({ action: actionKey, needsReason });
      setConfirmReason("");
      return;
    }
    if (needsReason) {
      setConfirmAction({ action: actionKey, needsReason: true });
      setConfirmReason("");
      return;
    }
    runWorkflowAction(actionKey);
  };

  const availableActions = useMemo(() => {
    if (!submission || !canManage) return [];
    const review = submission.reviewStatus;
    const actions = [];

    if (review === "new") actions.push(SELLER_WORKFLOW_ACTIONS.MARK_REVIEWED);
    if (["new", "reviewed", "needs_info", "contacted"].includes(review)) {
      actions.push(
        SELLER_WORKFLOW_ACTIONS.MARK_NEEDS_INFO,
        SELLER_WORKFLOW_ACTIONS.MARK_CONTACTED,
        SELLER_WORKFLOW_ACTIONS.APPROVE
      );
    }
    if (["new", "reviewed", "needs_info", "contacted", "approved"].includes(review)) {
      actions.push(SELLER_WORKFLOW_ACTIONS.REJECT, SELLER_WORKFLOW_ACTIONS.ARCHIVE);
    }
    if (["new", "reviewed"].includes(review)) {
      actions.push(SELLER_WORKFLOW_ACTIONS.MARK_SPAM);
    }
    if (["archived", "rejected", "spam"].includes(review)) {
      actions.push(SELLER_WORKFLOW_ACTIONS.REOPEN);
    }

    return [...new Set(actions)];
  }, [submission, canManage]);

  const conversionEligibility = useMemo(
    () => getSellerSubmissionConversionEligibility(submission),
    [submission]
  );

  const runConversion = async () => {
    if (!canManage || !conversionEligibility.ok) return;
    setConverting(true);
    setConversionMessage("");
    try {
      const data = await convertAdminSellerSubmissionToListing(submissionId, {
        confirm: true,
        internalNote: confirmReason || undefined,
      });
      mergeSubmission(data.submission);
      setNotesDraft(data.submission?.admin?.internalNotes || "");
      setConversionMessage(
        <>
          {data.message}{" "}
          {data.listingAdminUrl ? (
            <Link className="dealer-portal-btn" to={data.listingAdminUrl}>
              Ver borrador
            </Link>
          ) : null}
        </>
      );
    } catch (err) {
      setConversionMessage(err.message || "No pudimos convertir la solicitud.");
    } finally {
      setConverting(false);
      setConfirmAction(null);
      setConfirmReason("");
    }
  };

  const saveNotes = async () => {
    if (!canManage) return;
    setSavingNotes(true);
    setNotesMessage("");
    try {
      const data = await updateAdminSellerSubmissionNotes(submissionId, notesDraft);
      mergeSubmission(data.submission);
      setNotesDraft(data.submission?.admin?.internalNotes || "");
      setNotesMessage("Notas guardadas.");
    } catch (err) {
      setNotesMessage(err.message || "No pudimos guardar las notas.");
    } finally {
      setSavingNotes(false);
    }
  };

  if (loading) {
    return (
      <AdminPageLayout>
        <AdminStatePanel variant="loading" message="Cargando solicitud…" />
      </AdminPageLayout>
    );
  }

  if (!submission) {
    return (
      <AdminPageLayout>
        <AdminStatePanel variant="empty" message={error || "Solicitud no encontrada."} />
      </AdminPageLayout>
    );
  }

  const { seller, vehicle, pricing, condition, consent, attribution, guardrails, admin } = submission;

  return (
    <AdminPageLayout>
      <AdminPageHeader
        eyebrow="AutoMapa · Vendedores"
        title={formatVehicleSummary({
          vehicleYear: vehicle.year,
          vehicleMake: vehicle.make,
          vehicleModel: vehicle.model,
          vehicleTrim: vehicle.trim,
        })}
        description={`Solicitud ${submission.id}`}
      />
      <AdminAutomotiveNav active="seller-submissions" />

      <main className="admin-console__main wrap">
        <div className="import-actions">
          <Link className="dealer-portal-btn" to={SELLER_SUBMISSION_ADMIN_PATH}>
            Volver al inbox
          </Link>
        </div>

        <p>
          <span className={sellerReviewStatusBadgeClass(submission.reviewStatus)}>
            {formatSellerReviewStatus(submission.reviewStatus)}
          </span>{" "}
          <span className={sellerWorkflowStatusBadgeClass(submission.workflowStatus)}>
            {formatSellerWorkflowStatus(submission.workflowStatus)}
          </span>{" "}
          <span className="lead-badge">{formatSellerSubmissionStatus(submission.status)}</span>
        </p>

        {canManage ? (
          <DetailSection title="Acciones de revisión">
            <div className="import-actions" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
              {availableActions.map((action) => (
                <button
                  key={action}
                  type="button"
                  className={
                    action === SELLER_WORKFLOW_ACTIONS.APPROVE ||
                    action === SELLER_WORKFLOW_ACTIONS.MARK_REVIEWED
                      ? "dealer-portal-btn dealer-portal-btn--primary"
                      : "dealer-portal-btn"
                  }
                  disabled={workflowBusy}
                  onClick={() => openAction(action)}
                >
                  {SELLER_WORKFLOW_ACTION_LABELS_ES[action]}
                </button>
              ))}
              {conversionEligibility.ok ? (
                <button
                  type="button"
                  className="dealer-portal-btn dealer-portal-btn--primary"
                  disabled={workflowBusy || converting}
                  onClick={() => {
                    setConfirmAction({ action: "convert_listing", needsReason: true });
                    setConfirmReason("");
                  }}
                >
                  Convertir en borrador de publicación
                </button>
              ) : admin.convertedListingId ? (
                <Link className="dealer-portal-btn" to={`/admin/listings/${admin.convertedListingId}`}>
                  Ver borrador creado
                </Link>
              ) : (
                <button
                  type="button"
                  className="dealer-portal-btn"
                  disabled
                  title={sellerConversionBlockMessage(conversionEligibility.reason)}
                >
                  Convertir en borrador de publicación
                </button>
              )}
            </div>
            {conversionEligibility.ok ? (
              <p className="admin-table-subtext">
                Crea una publicación interna en estado borrador. No se publicará automáticamente.
              </p>
            ) : admin.convertedListingId ? null : (
              <p className="admin-table-subtext">{sellerConversionBlockMessage(conversionEligibility.reason)}</p>
            )}
            {conversionMessage ? <p className="admin-table-subtext">{conversionMessage}</p> : null}
            {workflowMessage ? <p className="admin-table-subtext">{workflowMessage}</p> : null}
            {!canManage ? null : (
              <p className="admin-table-subtext">
                Las acciones destructivas (rechazar, archivar, spam) requieren confirmación.
              </p>
            )}
          </DetailSection>
        ) : null}

        <div className="dealer-lead-grid">
          <DetailSection title="Vehículo">
            <DetailRow label="Marca" value={vehicle.make} />
            <DetailRow label="Modelo" value={vehicle.model} />
            <DetailRow label="Año" value={vehicle.year} />
            <DetailRow label="Versión" value={vehicle.trim} />
            <DetailRow
              label="Kilometraje"
              value={vehicle.mileageKm != null ? `${vehicle.mileageKm.toLocaleString("es-CO")} km` : null}
            />
            <DetailRow label="Relación" value={vehicle.ownershipStatus} />
            <DetailRow label="Fotos" value={vehicle.hasPhotos ? `Sí (${vehicle.photoCount})` : "No"} />
            {vehicle.plate ? (
              <p>
                <strong>Placa (interno):</strong> {vehicle.plate}
              </p>
            ) : null}
            {vehicle.vin ? (
              <p>
                <strong>VIN (interno):</strong> {vehicle.vin}
              </p>
            ) : null}
          </DetailSection>

          <DetailSection title="Contacto del vendedor">
            <DetailRow label="Nombre" value={seller.name} />
            <DetailRow label="Teléfono / WhatsApp" value={seller.phone} />
            <DetailRow label="Correo" value={seller.email} />
            <DetailRow label="Contacto preferido" value={seller.preferredContactMethod} />
            <DetailRow label="Tipo" value={formatSellerType(seller.type)} />
            <DetailRow label="Ciudad" value={seller.city} />
            <DetailRow label="Departamento" value={seller.department} />
          </DetailSection>

          <DetailSection title="Comunicación con el vendedor">
            <SellerSubmissionCommunicationPanel
              submissionId={submissionId}
              communication={submission.communication}
              seller={seller}
              canManage={canManage}
              onUpdated={(next) => mergeSubmission(next)}
            />
          </DetailSection>

          <DetailSection title="Precio y ubicación">
            <DetailRow label="Precio esperado" value={formatCopPrice(pricing.askingPrice, pricing.currency)} />
            <DetailRow label="Moneda" value={pricing.currency} />
          </DetailSection>

          <DetailSection title="Estado del vehículo">
            <DetailRow label="Resumen" value={condition.summary} />
            <DetailRow label="Notas del vendedor" value={condition.notes} />
          </DetailSection>

          <DetailSection title="Fotos del vendedor">
            <SellerSubmissionImageReviewSection
              submissionId={submissionId}
              images={submission.images || []}
              imageReview={submission.imageReview}
              convertedListingId={admin.convertedListingId}
              canManage={canManage}
              onUpdated={(patch) => {
                mergeSubmission({ ...submission, ...patch });
              }}
            />
          </DetailSection>

          <DetailSection title="Consentimiento">
            <DetailRow label="Consentimiento" value={consent.consentAccepted ? "Aceptado" : "No"} />
            <DetailRow label="Fecha de consentimiento" value={formatAdminDate(consent.consentAcceptedAt)} />
            <DetailRow label="Política" value={consent.privacyPolicyVersion} />
            <DetailRow label="Marketing opt-in" value={consent.marketingOptIn ? "Sí" : "No"} />
          </DetailSection>

          <DetailSection title="Atribución">
            <DetailRow label="Página origen" value={attribution.sourcePage} />
            <DetailRow label="Componente" value={attribution.sourceComponent} />
            <DetailRow label="Acción" value={attribution.sourceAction} />
            <DetailRow label="Ruta" value={attribution.currentPath} />
            <DetailRow label="Referrer" value={attribution.referrer} />
            <DetailRow label="UTM source" value={attribution.utmSource} />
            <DetailRow label="UTM medium" value={attribution.utmMedium} />
            <DetailRow label="UTM campaign" value={attribution.utmCampaign} />
            <DetailRow label="Enviado (cliente)" value={formatAdminDate(attribution.clientSubmittedAt)} />
          </DetailSection>

          <SubmissionContextSection
            context={{
              locale: attribution.locale,
              language: attribution.language,
              displayCurrency: attribution.displayCurrency,
              routeLocale: attribution.routeLocale,
              clientViewSlug: attribution.clientViewSlug,
              pageUrl: attribution.currentPath || attribution.sourcePage,
            }}
          />

          <DetailSection title="Guardrails">
            <DetailRow label="Estado spam" value={formatSellerSpamStatus(guardrails.spamStatus)} />
            <DetailRow
              label="Huella duplicado"
              value={guardrails.hasDuplicateFingerprint ? "Presente" : "No registrada"}
            />
          </DetailSection>

          <DetailSection title="Notas internas">
            <textarea
              className="admin-notes-textarea"
              rows={6}
              value={notesDraft}
              onChange={(event) => setNotesDraft(event.target.value)}
              disabled={!canManage || savingNotes}
              placeholder="Notas visibles solo para el equipo interno."
            />
            {canManage ? (
              <button type="button" className="dealer-portal-btn" onClick={saveNotes} disabled={savingNotes}>
                {savingNotes ? "Guardando…" : "Guardar notas"}
              </button>
            ) : (
              <p className="admin-table-subtext">No tienes permiso para editar notas.</p>
            )}
            {notesMessage ? <p className="admin-table-subtext">{notesMessage}</p> : null}
          </DetailSection>

          <DetailSection title="Auditoría interna">
            <DetailRow label="Motivo de estado" value={admin.statusReason} />
            <DetailRow label="Revisado" value={formatAdminDate(admin.reviewedAt)} />
            <DetailRow label="Contactado" value={formatAdminDate(admin.contactedAt)} />
            <DetailRow label="Aprobado" value={formatAdminDate(admin.approvedAt)} />
            <DetailRow label="Rechazado" value={formatAdminDate(admin.rejectedAt)} />
            <DetailRow label="Archivado" value={formatAdminDate(admin.archivedAt)} />
            <DetailRow label="Marcado spam" value={formatAdminDate(admin.spamMarkedAt)} />
            <DetailRow label="Flujo actualizado" value={formatAdminDate(admin.workflowUpdatedAt)} />
          </DetailSection>

          <DetailSection title="Conversión a publicación">
            <DetailRow label="Listing convertido" value={admin.convertedListingId} />
            <DetailRow label="Fecha conversión" value={formatAdminDate(admin.convertedAt)} />
            {admin.convertedListingId ? (
              <>
                <Link className="dealer-portal-btn" to={`/admin/listings/${admin.convertedListingId}`}>
                  Abrir borrador en admin
                </Link>
                {draftReview ? (
                  <div style={{ marginTop: "0.75rem" }}>
                    <span className={draftReviewStatusBadgeClass(draftReview.reviewStatuses?.draftReviewStatus)}>
                      {formatDraftReviewStatus(draftReview.reviewStatuses?.draftReviewStatus)}
                    </span>{" "}
                    <span className="lead-badge">
                      {formatDraftReviewStatus(draftReview.reviewStatuses?.publishReadinessStatus)}
                    </span>
                    {draftReview.quality?.missingFields?.length ? (
                      <p className="admin-table-subtext">
                        Pendiente: {draftReview.quality.missingFields.join(", ")}
                      </p>
                    ) : null}
                    {draftReview.quality?.warnings?.length ? (
                      <p className="admin-table-subtext">
                        Advertencias: {draftReview.quality.warnings.join(", ")}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </>
            ) : (
              <p className="admin-table-subtext">
                La conversión crea un borrador interno sin publicación automática.
              </p>
            )}
          </DetailSection>
        </div>

        <DetailSection title="Metadatos">
          <DetailRow label="Creado" value={formatAdminDate(submission.createdAt)} />
          <DetailRow label="Actualizado" value={formatAdminDate(submission.updatedAt)} />
        </DetailSection>
      </main>

      <ConfirmDialog
        open={Boolean(confirmAction)}
        title={
          confirmAction?.action === "convert_listing"
            ? "Convertir en borrador de publicación"
            : confirmAction?.action === "reject"
            ? "Rechazar solicitud"
            : confirmAction?.action === "mark_spam"
              ? "Marcar como spam"
              : confirmAction?.action === "archive"
                ? "Archivar solicitud"
                : "Confirmar acción"
        }
        message={
          confirmAction?.action === "convert_listing" ? (
            <div>
              <p>Se creará un borrador interno. No se publicará automáticamente.</p>
              <label>
                Nota interna (opcional)
                <textarea
                  rows={3}
                  value={confirmReason}
                  onChange={(event) => setConfirmReason(event.target.value)}
                  style={{ display: "block", width: "100%", marginTop: "0.5rem" }}
                />
              </label>
            </div>
          ) : confirmAction?.needsReason ? (
            <label>
              Nota o motivo interno (opcional)
              <textarea
                rows={3}
                value={confirmReason}
                onChange={(event) => setConfirmReason(event.target.value)}
                style={{ display: "block", width: "100%", marginTop: "0.5rem" }}
              />
            </label>
          ) : (
            `¿Confirmar "${SELLER_WORKFLOW_ACTION_LABELS_ES[confirmAction?.action] || confirmAction?.action}"?`
          )
        }
        confirmLabel="Confirmar"
        onCancel={() => {
          setConfirmAction(null);
          setConfirmReason("");
        }}
        onConfirm={() => {
          if (!confirmAction) return;
          if (confirmAction.action === "convert_listing") {
            runConversion();
            return;
          }
          runWorkflowAction(confirmAction.action, {
            reason: confirmReason || undefined,
            internalNote: confirmReason || undefined,
          });
        }}
      />
    </AdminPageLayout>
  );
}

export default function AdminSellerSubmissionDetailPage() {
  return (
    <RequirePermission permission={ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_VIEW}>
      <AdminSellerSubmissionDetailPageContent />
    </RequirePermission>
  );
}
